import React from "react";
import { ArrowRight, Phone } from "lucide-react";
import InquiryForm from "@/components/sections/InquiryForm";
import { trackEvent } from "@/lib/analytics";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770425910016-4b1b45991db9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjcxMzY3M3ww&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#F5F5F7]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Moderní kancelářský interiér"
          className="w-full h-full object-cover"
          style={{ filter: "brightness(1.22) contrast(0.92) saturate(0.95)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(46,52,60,0.42) 0%, rgba(46,52,60,0.28) 45%, rgba(30,36,44,0.62) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 78% 18%, rgba(27,147,164,0.18) 0%, rgba(27,147,164,0) 55%)",
          }}
        />
      </div>

      {/* Grid lines accent */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-28 md:pt-32 pb-16 md:pb-24 min-h-[100svh] flex flex-col">
        {/* Top meta */}
        <div className="reveal flex items-center justify-between text-white/80 text-xs uppercase tracking-[0.28em] font-semibold">
          <span data-testid="hero-overline">
            Spolehlivý úklid kanceláří a objektů v Brně
          </span>
          <span className="hidden md:block tabular-nums">
            [ EST. BRNO / CZ ]
          </span>
        </div>

        {/* Main content: 2-col on lg+ */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-12 md:mt-16 items-start">
          {/* Left: headline + CTAs */}
          <div className="lg:col-span-7">
            <h1 className="reveal" style={{ animationDelay: "0.05s" }}>
              <span className="block font-display font-black text-white text-[clamp(2.4rem,6.5vw,5.8rem)] leading-[0.95] tracking-[-0.03em]">
                Úklid,
              </span>
              <span className="block font-display font-black text-white text-[clamp(2.4rem,6.5vw,5.8rem)] leading-[0.95] tracking-[-0.03em]">
                který nemusíte
              </span>
              <span
                className="block font-display font-black text-[clamp(2.4rem,6.5vw,5.8rem)] leading-[0.95] tracking-[-0.03em] text-[#1B93A4] italic"
                style={{ fontWeight: 900 }}
              >
                řešit.
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="reveal mt-7 md:mt-9 max-w-xl text-white text-[19px] md:text-[22px] leading-[1.75] font-light"
              style={{ animationDelay: "0.2s" }}
            >
              Pravidelný úklid kanceláří, administrativních budov a provozoven
              v Brně a okolí. Spolehlivý servis, rychlá komunikace
              a individuální systém úklidu pro každý objekt.
            </p>

            <div
              className="reveal mt-8 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "0.35s" }}
            >
              <a
                href="#calc"
                data-testid="hero-cta-primary"
                onClick={(e) => {
                  trackEvent("cta_click_spocitat_uklid", { location: "hero_primary" });
                  const el = document.getElementById("calc");
                  if (el) {
                    e.preventDefault();
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className="group inline-flex items-center gap-3 bg-white text-[#111111] px-7 h-14 font-semibold tracking-tight hover:bg-[#0F5C69] hover:text-white transition-colors"
              >
                Rychlá nabídka
                <ArrowRight
                  size={18}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="tel:+420775601254"
                data-testid="hero-cta-phone"
                onClick={() => trackEvent("cta_click_phone", { location: "hero" })}
                className="inline-flex items-center gap-3 bg-transparent text-white border border-white/40 px-7 h-14 font-semibold tracking-tight hover:bg-white/10 transition-colors"
              >
                <Phone size={16} strokeWidth={2.5} />
                <span className="tabular-nums">+420 775 601 254</span>
              </a>
            </div>
          </div>

          {/* Right: inquiry form */}
          <div
            className="lg:col-span-5 reveal"
            style={{ animationDelay: "0.4s" }}
          >
            <InquiryForm />
          </div>
        </div>

        {/* Bottom strip removed per user request */}
      </div>
    </section>
  );
};

export default Hero;
